import { useCallback, useState } from "react";
import { toKey } from "../components/editModalFunctions";
import type { SuggestItemDetailsRequest } from "../services/vendorHelpers";
import { resolveProductTypeSelection } from "../lib/productTypeSelection";

export type AiSuggestionsState = {
  manufacturer?: string | null;
  model?: string | null;
  productType?: string | null;
  loading: boolean;
  error?: string;
};

type Params = {
  suggestItemDetailsCallable: (args: SuggestItemDetailsRequest) => Promise<any>;
};

type RequestArgs = {
  serial: string;
  manufacturer?: string;
  productTypes: string[];
  manufacturerList: string[];
  modelList: string[];
  rpTypeOpts: { id: string; label: string }[];
  setForm: React.Dispatch<React.SetStateAction<any>>;
};

export function useAiSuggestions({ suggestItemDetailsCallable }: Params) {
  const [aiSuggestions, setAiSuggestions] = useState<AiSuggestionsState>({ loading: false });

  const requestSuggestions = useCallback(async ({
    serial,
    manufacturer,
    productTypes,
    manufacturerList,
    modelList,
    rpTypeOpts,
    setForm,
  }: RequestArgs) => {
    setAiSuggestions((prev) => ({ ...prev, loading: true, error: undefined }));
    try {
      const res = await suggestItemDetailsCallable({
        serial,
        manufacturer: manufacturer || undefined,
        productTypes,
      });
      const data = res?.data;
      if (data?.ok) {
        setAiSuggestions((prev) => ({
          ...prev,
          loading: false,
          manufacturer: (data as any).suggestedManufacturer ?? null,
          model: (data as any).suggestedModel ?? null,
          productType: (data as any).suggestedProductType ?? null,
        }));

        setForm((p: any) => {
          const pick = (list: any[] | undefined, s: string | null | undefined, fb: string) => {
            if (!s) return fb;
            const found = (list || []).find((x: any) => toKey(String(x)) === toKey(String(s)));
            return (found ?? s) as string;
          };
          const exactManu = pick(manufacturerList, (data as any).suggestedManufacturer, p.manufacturer);
          const exactModel = pick(modelList, (data as any).suggestedModel, p.model);
          const exactPT = pick((rpTypeOpts || []).map((t) => t.label), (data as any).suggestedProductType, p.productType);
          const productTypeSelection = resolveProductTypeSelection(rpTypeOpts, exactPT || p.productType);

          return {
            ...p,
            manufacturer: p.manufacturer || exactManu || "",
            model: p.model || exactModel || "",
            ...(p.productType
              ? { productType: p.productType, productTypeId: p.productTypeId || "" }
              : productTypeSelection),
          };
        });
      } else {
        setAiSuggestions((prev) => ({ ...prev, loading: false, error: "Inga AI-förslag kunde hämtas." }));
      }
    } catch (e: any) {
      setAiSuggestions((prev) => ({ ...prev, loading: false, error: e?.message || "AI-förslag misslyckades." }));
    }
  }, [suggestItemDetailsCallable]);

  return { aiSuggestions, setAiSuggestions, requestSuggestions };
}
